import React, { useState, useEffect, useRef } from 'react';
import { EndeavorApi } from '../../services/api/EndeavorApi';
import { InviteApi } from '../../services/api/InviteApi';
import './CourseMatchBanner.css';

/**
 * CourseMatchBanner — shown during onboarding when a user enters course info.
 * Debounce-searches for matching shared courses and lets the user join one.
 *
 * Props:
 *   courseName  — current course name text
 *   instructor  — current instructor text
 *   onJoined    — callback({ endeavorId, endeavorName }) when user successfully joins
 */
export default function CourseMatchBanner({ courseName, instructor, onJoined }) {
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [joinTarget, setJoinTarget] = useState(null); // endeavorId being joined
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [joining, setJoining] = useState(false);
  const debounceRef = useRef(null);

  // Debounced search when courseName or instructor changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const name = (courseName || '').trim();
    const instr = (instructor || '').trim();

    if (name.length < 3 && instr.length < 3) {
      setMatches([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const params = {};
        if (name.length >= 3) params.courseName = name;
        if (instr.length >= 3) params.instructor = instr;
        const resp = await EndeavorApi.searchSharedCourses(params);
        if (resp.ok) {
          const data = await resp.json();
          setMatches(Array.isArray(data) ? data : []);
        } else {
          setMatches([]);
        }
      } catch {
        setMatches([]);
      } finally {
        setSearching(false);
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [courseName, instructor]);

  const handleJoinClick = (endeavorId) => {
    setJoinTarget(endeavorId);
    setJoinCode('');
    setJoinError('');
  };

  const handleRedeem = async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    setJoinError('');
    try {
      const resp = await InviteApi.redeemInvite(joinCode.trim());
      if (resp.ok) {
        const match = matches.find((m) => m.endeavorId === joinTarget);
        onJoined?.({
          endeavorId: joinTarget,
          endeavorName: match?.name || 'Shared Course',
        });
      } else {
        const err = await resp.json().catch(() => ({}));
        setJoinError(err.message || 'Invalid or expired invite code');
      }
    } catch {
      setJoinError('Failed to join course');
    } finally {
      setJoining(false);
    }
  };

  if (matches.length === 0 && !searching) return null;

  return (
    <div className="course-match-banner">
      <div className="cmb-header">
        <span className="cmb-icon">👥</span>
        <span className="cmb-title">
          {searching ? 'Searching for shared courses…' : 'Matching shared courses found!'}
        </span>
      </div>

      {!searching && matches.length > 0 && (
        <p className="cmb-hint">
          A professor may have already created this course. Join theirs to stay in sync.
        </p>
      )}

      <div className="cmb-results">
        {matches.map((m) => (
          <div key={m.endeavorId} className="cmb-card">
            <div className="cmb-card-info">
              <strong>{m.name}</strong>
              {m.organizer && <span className="cmb-instructor">by {m.organizer}</span>}
            </div>

            {joinTarget === m.endeavorId ? (
              <div className="cmb-join-form">
                <input
                  type="text"
                  className="cmb-code-input"
                  placeholder="Enter invite code"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
                  disabled={joining}
                  autoFocus
                />
                <button
                  className="cmb-redeem-btn"
                  onClick={handleRedeem}
                  disabled={joining || !joinCode.trim()}
                >
                  {joining ? 'Joining…' : 'Join'}
                </button>
                <button
                  className="cmb-cancel-btn"
                  onClick={() => setJoinTarget(null)}
                  disabled={joining}
                >
                  Cancel
                </button>
                {joinError && <p className="cmb-error">{joinError}</p>}
              </div>
            ) : (
              <button className="cmb-join-btn" onClick={() => handleJoinClick(m.endeavorId)}>
                Join
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
